import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  useTheme,
  IconButton,
  TextField,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tokens } from '../../theme';
import { can } from '../../utils/permissions';

const formatEuro = (value) =>
  `€ ${value != null && !Number.isNaN(Number(value)) ? Number(value).toFixed(2) : '0.00'}`;

const DestinationReportDetails = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { countryId } = useParams();

  const [summary, setSummary] = useState(null);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const handleExportExcel = async () => {
    setExportError('');
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }
      const url = `${process.env.REACT_APP_APILINK}/api/admin/reports/destinations/${countryId}/export-excel${
        params.toString() ? `?${params.toString()}` : ''
      }`;
      const res = await fetch(url, { headers: { token } });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setExportError(err.message || 'Export failed');
        return;
      }
      const blob = await res.blob();
      const disp = res.headers.get('Content-Disposition');
      const match = disp && disp.match(/filename="?([^";\n]+)"?/);
      const filename = match ? match[1].trim() : `destination-report_${countryId}.xlsx`;
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(href);
    } catch (e) {
      console.error('Export Excel error', e);
      setExportError('Export failed');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const params = new URLSearchParams();
        if (startDate && endDate) {
          params.append('startDate', startDate);
          params.append('endDate', endDate);
        }
        const url = `${process.env.REACT_APP_APILINK}/api/admin/reports/destinations/${countryId}${
          params.toString() ? `?${params.toString()}` : ''
        }`;
        const res = await fetch(url, {
          headers: { token },
        });
        const data = await res.json();
        if (data.success) {
          setSummary(data.data.summary);
          setGuides(data.data.guides || []);
        } else {
          setSummary(null);
        }
      } catch (e) {
        console.error('Error fetching destination report details', e);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [countryId, startDate, endDate]);

  const guideColumns = [
    {
      field: 'guideName',
      headerName: 'Guide',
      flex: 1.4,
      minWidth: 160,
      renderCell: (params) => (
        <Box
          component="span"
          sx={{
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {params.value ?? ''}
        </Box>
      ),
    },
    {
      field: 'departureDate',
      headerName: 'Departure',
      flex: 0.8,
      minWidth: 110,
      valueFormatter: ({ value }) => value || '-',
    },
    {
      field: 'returnDate',
      headerName: 'Return',
      flex: 0.8,
      minWidth: 110,
      valueFormatter: ({ value }) => value || '-',
    },
    {
      field: 'bus',
      headerName: 'Bus',
      flex: 0.6,
      minWidth: 80,
      valueFormatter: ({ value }) => value || '-',
    },
    { field: 'totalPassengers', headerName: 'Passengers', flex: 0.7, minWidth: 100 },
    {
      field: 'collectedAmount',
      headerName: 'Collected amount (€)',
      flex: 0.9,
      minWidth: 150,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'guideFee',
      headerName: 'Guide fee (€)',
      flex: 0.85,
      minWidth: 130,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'averageSalesPerGuide',
      headerName: 'Avg sales / passenger (€)',
      flex: 0.95,
      minWidth: 170,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'toBeSettled',
      headerName: 'To be settled (€)',
      flex: 0.9,
      minWidth: 150,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'totalAdditionalIncome',
      headerName: 'Total Additional Income',
      flex: 1,
      minWidth: 175,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'destinationRelatedCosts',
      headerName: 'Destination-Related Costs',
      flex: 1,
      minWidth: 185,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'totalOtherIncome',
      headerName: 'Total Other Income',
      flex: 0.95,
      minWidth: 155,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'totalOtherCost',
      headerName: 'Total Other Cost',
      flex: 0.9,
      minWidth: 145,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'netSettlementAmount',
      headerName: 'Net Settlement Amount',
      flex: 1,
      minWidth: 175,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'guideAllowance',
      headerName: 'Guide Allowance',
      flex: 0.9,
      minWidth: 145,
      valueFormatter: (params) =>
        `€ ${params.value != null ? Number(params.value).toFixed(2) : '0.00'}`,
    },
    {
      field: 'mileage',
      headerName: 'Mileage',
      flex: 0.75,
      minWidth: 100,
      valueFormatter: (params) =>
        params.value != null ? Number(params.value).toLocaleString() : '0',
    },
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box m="20px">
        <Box display="flex" alignItems="center" mb={3}>
          <IconButton onClick={() => navigate('/reports/destinations')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h3" color={colors.grey[100]} fontWeight="bold">
            Destination Report
          </Typography>
        </Box>
        <Card
          sx={{
            backgroundColor: colors.primary[400],
            maxWidth: 480,
            mx: 'auto',
            textAlign: 'center',
            py: 4,
            px: 3,
          }}
        >
          <CardContent>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: colors.primary[500],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <AssessmentOutlinedIcon sx={{ fontSize: 36, color: colors.grey[400] }} />
            </Box>
            <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" gutterBottom>
              No report found
            </Typography>
            <Typography variant="body1" color={colors.grey[300]} sx={{ mb: 3 }}>
              There’s no data for this destination{startDate && endDate ? ' in the selected date range' : ''}. Try
              adjusting the dates or choose another destination.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/reports/destinations')}
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                fontWeight: 600,
                px: 3,
                py: 1.5,
                '&:hover': { backgroundColor: colors.blueAccent[700] },
              }}
            >
              Back to Destination Reports
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/reports/destinations')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h3" color={colors.grey[100]} fontWeight="bold">
            {summary.countryName} - Destination Report
          </Typography>
        </Box>

        {/* Date filters */}
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <Box>
            <Typography variant="body2" color={colors.grey[200]} mb={0.5}>
              Start Date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: colors.primary[500], minWidth: 160 }}
            />
          </Box>
          <Box>
            <Typography variant="body2" color={colors.grey[200]} mb={0.5}>
              End Date
            </Typography>
            <TextField
              type="date"
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ backgroundColor: colors.primary[500], minWidth: 160 }}
            />
          </Box>
          <Box mt={3} display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
              sx={{
                borderRadius: 1,
                px: 2,
                py: 1,
                backgroundColor: colors.primary[500],
                '&:hover': { backgroundColor: colors.primary[600] },
              }}
            >
              <Typography variant="body2" color={colors.grey[100]}>
                Clear
              </Typography>
            </IconButton>
            {can('reports.destination.export') && (
              <Button
                variant="contained"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={handleExportExcel}
                disabled={exporting}
                sx={{
                  backgroundColor: colors.greenAccent[600],
                  color: colors.grey[100],
                  '&:hover': { backgroundColor: colors.greenAccent[700] },
                }}
              >
                {exporting ? 'Exporting…' : 'Export Excel'}
              </Button>
            )}
          </Box>
          {exportError && (
            <Box width="100%">
              <Typography variant="body2" color={colors.redAccent[500]} sx={{ mt: 1 }}>
                {exportError}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Summary cards */}
      <Grid container spacing={3} mb={3}>
        {[
          {
            label: 'Passengers',
            value: summary.totalPassengers ?? 0,
            isCount: true,
            color: colors.greenAccent[500],
          },
          {
            label: 'Collected Amount',
            value: formatEuro(summary.collectedAmount ?? summary.totalRevenue),
            color: colors.greenAccent[500],
          },
          {
            label: 'Guide Fee',
            value: formatEuro(summary.guideFee),
            color: colors.blueAccent[500],
          },
          {
            label: 'Total Additional Income',
            value: formatEuro(summary.totalAdditionalIncome),
            color: colors.greenAccent[400],
          },
          {
            label: 'Destination-Related Costs',
            value: formatEuro(summary.destinationRelatedCosts),
            color: colors.redAccent[400],
          },
          {
            label: 'Total Other Income',
            value: formatEuro(summary.totalOtherIncome),
            color: colors.greenAccent[400],
          },
          {
            label: 'Total Other Costs',
            value: formatEuro(summary.totalOtherCost),
            color: colors.redAccent[400],
          },
          {
            label: 'Net Settlement Amount',
            value: formatEuro(summary.netSettlementAmount),
            color: colors.blueAccent[400],
          },
          {
            label: 'Mileage',
            value: Number(summary.totalMileage ?? 0).toLocaleString(),
            isCount: true,
            color: colors.blueAccent[300],
          },
        ].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card sx={{ backgroundColor: colors.primary[400], height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" color={colors.grey[300]}>
                  {card.label}
                </Typography>
                <Typography variant="h5" color={card.color} fontWeight="bold" sx={{ mt: 0.5 }}>
                  {card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Guides (aggregated) */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: colors.primary[400], height: '100%' }}>
            <CardContent>
              <Typography variant="h5" color={colors.grey[100]} fontWeight="bold" mb={1}>
                Guides (Per Departure)
              </Typography>
              <Typography variant="caption" color={colors.grey[300]} sx={{ display: 'block', mb: 2 }}>
                Guide allowance is for record-keeping only and is not included in net settlement or other totals.
              </Typography>
              <Box
                height={480}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  '& .MuiDataGrid-root': { border: 'none' },
                  '& .MuiDataGrid-cell': { borderBottom: 'none' },
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: colors.blueAccent[700],
                    borderBottom: 'none',
                  },
                  '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
                  '& .MuiDataGrid-footerContainer': {
                    borderTop: 'none',
                    backgroundColor: colors.blueAccent[700],
                  },
                }}
              >
                <DataGrid
                  rows={guides.map((g, idx) => ({
                    ...g,
                    id: g.guideId && g.departureDate
                      ? `${g.guideId}_${g.departureDate}_${g.bus || ''}`
                      : `guide-${idx}`,
                  }))}
                  columns={guideColumns}
                  getRowId={(row) => row.id}
                  pageSize={10}
                  rowsPerPageOptions={[10]}
                  disableSelectionOnClick
                  autoHeight={false}
                  onRowClick={(params) => {
                    if (params.row.guideId) {
                      // Scope to this row's own departure date so each departure opens its own
                      // report instead of merging every trip this guide ran at this destination.
                      const rowDate = params.row.departureDate;
                      const query = rowDate
                        ? `?startDate=${encodeURIComponent(rowDate)}&endDate=${encodeURIComponent(rowDate)}`
                        : startDate && endDate
                          ? `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
                          : '';
                      navigate(`/reports/destinations/${countryId}/guides/${params.row.guideId}${query}`);
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DestinationReportDetails;

